import React, { useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import List from '@mui/material/List';
import ListItem from '@mui/material/ListItem';
import ListItemText from '@mui/material/ListItemText';
import ListItemButton from '@mui/material/ListItemButton';
import ListItemAvatar from '@mui/material/ListItemAvatar';
import Avatar from '@mui/material/Avatar';
import useElDimensions from '../hooks/useElDimensions';

import {
  selectResToPublicDataMap,
  selectPublicData,
  selectSelectedPublicData,
  selectSelectedProduct,
  selectAppConfigProp,
} from '../slices/appState';
import { useDispatch, useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { Typography } from '@mui/material';

/**
 * Whether a resource can deliver the given product.
 *
 * Tri-state on purpose: `resource.supportedProducts` comes straight from public
 * data and may be absent, and the two consumers need opposite defaults — the
 * switch confirmation must only trigger on a definitive `false`, the highlight
 * only on a definitive `true`.
 *
 * @returns {boolean|null} true = supports, false = does not, null = unknown
 */
export function resourceSupportsProduct(publicData, product) {
  if (!product?.uuid) return null;
  // the synthetic "all" entry aggregates every resource
  if (publicData?.resource?.uuid === 'all-option') return true;

  const supportedProducts = publicData?.resource?.supportedProducts;
  if (!Array.isArray(supportedProducts)) return null;

  return supportedProducts.includes(product.uuid);
}

/**
 * Whether a timeslot must be kept out of the list for the selected product.
 *
 * A slot carrying a `products` stamp names the products it serves outright and
 * is always left alone — including when the resource's own catalogue omits
 * them, which is exactly the case that has to survive. An *unstamped* slot
 * means "the default products of its resource", and that attribution is what
 * the "all resources" fetch loses: one `prdRef`, many `ref`s, so the backend
 * cannot say whose defaults an unstamped slot belongs to. We supply it here.
 *
 * An unstamped slot serves its resource's *non-exclusive* products, so both
 * terms are needed: `supportedProducts` is a catalogue of everything a resource
 * can deliver — exclusive products included — and therefore cannot tell a
 * default service apart from one that is only reachable through a stamped slot.
 * `product.exclusive` is what draws that line.
 *
 * Filtering *by* product stays the backend's job (see the commit that moved it
 * there, "This filtering was moved into the backend"); this only supplies the
 * resource attribution the all-resources fetch cannot express.
 */
export function isHiddenForSelectedProduct(timeslot, product, publicDataMap) {
  // the customer's own appointment always shows
  if (timeslot?.isCancelable) return false;
  // stamped: the slot names its own products, so it is not ours to judge
  if (timeslot?.products?.length > 0) return false;

  // an exclusive product is reachable only through a slot set up for it
  if (product?.exclusive) return true;

  const publicData = publicDataMap?.[timeslot?.resource_uuid];
  return resourceSupportsProduct(publicData, product) === false;
}

export default function ResourceSelector({
  onResourceSelected,
  headerVariant = 'h4',
  height,
  HeadlineProps = {},
  ListProps = {},
  ListItemProps = {},
  ListItemButtonProps = {},
}) {
  const dispatch = useDispatch();
  const { t } = useTranslation();
  const publicDataMap = useSelector(selectResToPublicDataMap);
  const publicData = useSelector(selectSelectedPublicData);
  const product = useSelector(selectSelectedProduct);
  const ResourceSwitchConfirmDialog = useSelector((state) =>
    selectAppConfigProp(state, 'interfaces.ResourceSwitchConfirmDialog'),
  );
  const constrainDialogsToContainer = useSelector((state) =>
    selectAppConfigProp(state, 'constrainDialogsToContainer'),
  );
  const [tslHeadlineRef, { height: headlineHeight }] = useElDimensions();
  const [pendingPublicData, setPendingPublicData] = useState(null);

  const hasResourcesWithImages = useMemo(() => {
    if (!publicDataMap) return false;

    return !!Object.entries(publicDataMap).find(
      ([, data]) => !!data.resource?.imgUrl,
    );
  }, [publicDataMap]);

  // Which resources can still deliver the chosen service — the dialog names
  // them, because "where else can I get this?" is the customer's real question.
  // The synthetic "all" entry is skipped: it supports everything by definition,
  // so listing it would say nothing.
  const supportingResourceNames = useMemo(() => {
    if (!publicDataMap || !product?.uuid) return [];

    return Object.values(publicDataMap)
      .filter(
        (data) =>
          data?.resource?.uuid !== 'all-option' &&
          resourceSupportsProduct(data, product) === true,
      )
      .map((data) => data?.resource?.name)
      .filter(Boolean);
  }, [publicDataMap, product]);

  const commitPick = (data) => {
    dispatch(selectPublicData(data));
    if (onResourceSelected) {
      onResourceSelected(data);
    }
  };

  const handlePick = (data) => {
    const isCurrent = publicData?.resource?.uuid === data?.resource?.uuid;

    // Switching away from the chosen product is destructive, so ask first.
    // Unknown support (no supportedProducts in public data) never blocks —
    // Calendar still reconciles once the new product list arrives.
    if (
      !isCurrent &&
      ResourceSwitchConfirmDialog &&
      resourceSupportsProduct(data, product) === false
    ) {
      setPendingPublicData(data);
      return;
    }

    commitPick(data);
  };

  if (!publicDataMap) return;

  return (
    <>
      <Typography
        ref={tslHeadlineRef}
        variant={headerVariant}
        component={'h2'}
        {...HeadlineProps}
        sx={{
          mb: 1,
          ...HeadlineProps?.sx,
        }}
      >
        {t('resource_selection_headline')}
      </Typography>
      <List
        dense
        {...ListProps}
        sx={{
          p: 0,
          maxHeight: `${height - headlineHeight}px`,
          overflowY: 'auto',
          ...ListProps?.sx,
        }}
      >
        {Object.entries(publicDataMap).map(([, data]) => {
          const isSelected =
            !!publicData && publicData?.resource.uuid === data?.resource.uuid;
          const supportsProduct =
            resourceSupportsProduct(data, product) === true;

          return (
            <ListItem
              key={data?.ref}
              {...ListItemProps}
              disablePadding
              sx={[
                {
                  mx: 'auto',
                  marginBottom: '8px',
                  minHeight: '36.5px',
                  // transparent baseline so marking the selection adds no shift
                  border: '1px solid transparent',
                },
                { ...ListItemProps.sx },
                // last, so it also wins over a caller-supplied border colour
                isSelected && { borderColor: 'primary.main' },
              ]}
            >
              <ListItemButton
                // every resource that can deliver the chosen product shares the
                // selected background; the border above marks the actual one
                selected={isSelected || supportsProduct}
                onClick={() => {
                  handlePick(data);
                }}
                {...ListItemButtonProps}
              >
                {data?.resource?.imgUrl && (
                  <ListItemAvatar>
                    <Avatar
                      src={data.resource?.imgUrl}
                      alt={data.resource?.name}
                    />
                  </ListItemAvatar>
                )}
                <ListItemText
                  inset={!data?.resource?.imgUrl && hasResourcesWithImages}
                  primary={data?.resource?.name}
                  slotProps={{
                    primary: { variant: 'body2' },
                  }}
                />
              </ListItemButton>
            </ListItem>
          );
        })}
      </List>
      {ResourceSwitchConfirmDialog && (
        <ResourceSwitchConfirmDialog
          open={!!pendingPublicData}
          resourceName={pendingPublicData?.resource?.name}
          productName={product?.name}
          supportingResourceNames={supportingResourceNames}
          constrainDialogsToContainer={constrainDialogsToContainer}
          onCancel={() => setPendingPublicData(null)}
          onConfirm={() => {
            const data = pendingPublicData;
            setPendingPublicData(null);
            commitPick(data);
          }}
        />
      )}
    </>
  );
}

ResourceSelector.displayName = 'ResourceSelector';
ResourceSelector.propTypes = {
  /** callback for parent to trigger action on produc selection */
  onProductSelected: PropTypes.func,
};
